import React, { useEffect, useReducer, useState } from 'react';
import { VALIDATION_STATES } from '../../utils/constants';
import Address1 from '../Fields/Address1';
import Address2 from '../Fields/Address2';
import City from '../Fields/City';
import Country from '../Fields/Country';
import PostalCode from '../Fields/PostalCode';
import State from '../Fields/State';
import { GridContainer } from '@parchment/atoms';
import AddressVerification from './AddressVerification';
import { getListOfCountryCodes, getListOfStateCodes } from '../../api/addressVerificationApi';
const noop = () => undefined;
const BLANK_ADDRESS = {
  verified: false,
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
  country: ''
};
export default function AddressGrid(_ref) {
  let {
    address = BLANK_ADDRESS,
    required = false,
    disabled = false,
    disable = [],
    onChange = noop,
    onValidate = noop,
    autocomplete = false,
    verify = true,
    hideValidAddressBanner = false,
    root
  } = _ref;
  const initialAddressFromProps = {
    address1: address.address1,
    address2: address.address2,
    city: address.city,
    country: address.country,
    state: address.state,
    zip: address.zip
  };
  const initialValidationStates = {
    address1: VALIDATION_STATES.neutral,
    address2: VALIDATION_STATES.neutral,
    city: VALIDATION_STATES.neutral,
    country: VALIDATION_STATES.neutral,
    state: VALIDATION_STATES.neutral,
    zip: VALIDATION_STATES.neutral
  };
  const wrapper = /*#__PURE__*/React.createRef();
  const [countryOptions, setCountryOptions] = useState({
    isLoading: true,
    options: []
  });
  const [stateOptions, setStateOptions] = useState({
    isLoading: true,
    options: []
  });
  const [isLoading, setIsLoading] = useState(true);
  const [autoSelectVerified, setAutoSelectVerified] = useState(false);
  const [addressData, setAddressData] = useReducer((values, newValues) => ({
    ...values,
    ...newValues
  }), initialAddressFromProps);
  const [addressValidation, setAddressValidation] = useReducer((values, newValues) => ({
    ...values,
    ...newValues
  }), initialValidationStates);
  const [userModified, setUserModified] = useState(false);
  useEffect(() => {
    getListOfCountryCodes(root).then(countries => {
      const options = countries.map(country => ({
        value: country.iso2,
        label: country.name,
        data: country
      }));
      setCountryOptions({
        isLoading: false,
        options: options
      });
    }).catch(() => setCountryOptions({
      isLoading: false,
      options: []
    }));
  }, []);
  useEffect(() => {
    if (!countryOptions.isLoading) {
      if (addressData.country) {
        getListOfStateCodes(addressData.country, root).then(states => {
          const options = (states === null || states === void 0 ? void 0 : states.map(state => ({
            value: state.abbreviation,
            label: state.name,
            data: state
          }))) || [];
          setStateOptions({
            isLoading: false,
            options: options
          });
        }).catch(() => setStateOptions({
          isLoading: false,
          options: []
        }));
        if (userModified) {
          handleFormChange('state', '');
        }
      } else {
        setStateOptions({
          isLoading: false,
          options: []
        });
      }
    }
  }, [countryOptions.isLoading, addressData.country]);
  useEffect(() => {
    setIsLoading(countryOptions.isLoading && stateOptions.isLoading);
  }, [countryOptions, stateOptions]);
  useEffect(() => onChange(addressData), [addressData]);
  useEffect(() => {
    const states = Object.values(addressValidation).map(field => field);
    if (states.indexOf(VALIDATION_STATES.error) >= 0) {
      return onValidate(VALIDATION_STATES.error);
    } else if (states.indexOf(VALIDATION_STATES.neutral) >= 0) {
      return onValidate(VALIDATION_STATES.neutral);
    } else if (states.indexOf(VALIDATION_STATES.pending) >= 0) {
      return onValidate(VALIDATION_STATES.pending);
    } else if (states.indexOf(VALIDATION_STATES.success) >= 0) {
      return onValidate(VALIDATION_STATES.success);
    }
  }, [addressValidation]);
  const handleFormChange = (field, value) => {
    setAddressData({
      [field]: value,
      verified: false
    });
  };
  const handleValidationChange = (field, validationState) => {
    setAddressValidation({
      [field]: validationState
    });
  };
  return /*#__PURE__*/React.createElement("div", {
    ref: wrapper
  }, /*#__PURE__*/React.createElement(GridContainer, {
    "data-foo": "bar"
  }, /*#__PURE__*/React.createElement("div", {
    onClick: () => setUserModified(true)
  }, /*#__PURE__*/React.createElement(Country, {
    value: addressData.country,
    loading: isLoading,
    disabled: disabled || (disable === null || disable === void 0 ? void 0 : disable.includes('country')),
    required: required,
    onChange: value => handleFormChange('country', value || ''),
    onValidate: validationState => handleValidationChange('country', validationState),
    onFocus: () => setAutoSelectVerified(false),
    onBlur: () => setTimeout(() => setAutoSelectVerified(true), 300),
    options: countryOptions.options
  }))), /*#__PURE__*/React.createElement(GridContainer, {
    "data-foo": "bar"
  }, /*#__PURE__*/React.createElement(Address1, {
    value: addressData.address1,
    loading: isLoading,
    disabled: disabled || (disable === null || disable === void 0 ? void 0 : disable.includes('address1')),
    internalLabel: true,
    required: required,
    onChange: value => handleFormChange('address1', value || ''),
    onValidate: validationState => handleValidationChange('address1', validationState),
    onFocus: () => setAutoSelectVerified(false),
    onBlur: () => setTimeout(() => setAutoSelectVerified(true), 300),
    width: "100%"
  }), /*#__PURE__*/React.createElement(Address2, {
    value: addressData.address2,
    loading: isLoading,
    disabled: disabled || (disable === null || disable === void 0 ? void 0 : disable.includes('address2')),
    internalLabel: true,
    onChange: value => handleFormChange('address2', value || ''),
    onValidate: validationState => handleValidationChange('address2', validationState),
    onFocus: () => setAutoSelectVerified(false),
    onBlur: () => setTimeout(() => setAutoSelectVerified(true), 300),
    width: "100%"
  }), /*#__PURE__*/React.createElement(City, {
    value: addressData.city,
    loading: isLoading,
    disabled: disabled || (disable === null || disable === void 0 ? void 0 : disable.includes('city')),
    internalLabel: true,
    required: required,
    onChange: value => handleFormChange('city', value || ''),
    onValidate: validationState => handleValidationChange('city', validationState),
    onFocus: () => setAutoSelectVerified(false),
    onBlur: () => setTimeout(() => setAutoSelectVerified(true), 300)
  }), /*#__PURE__*/React.createElement(State, {
    value: addressData.state,
    loading: isLoading,
    disabled: disabled || (disable === null || disable === void 0 ? void 0 : disable.includes('state')),
    internalLabel: true,
    options: stateOptions.options,
    onChange: value => handleFormChange('state', value || ''),
    onValidate: validationState => handleValidationChange('state', validationState),
    onFocus: () => setAutoSelectVerified(false),
    onBlur: () => setTimeout(() => setAutoSelectVerified(true), 300)
  }), /*#__PURE__*/React.createElement(PostalCode, {
    value: addressData.zip,
    loading: isLoading,
    disabled: disabled || (disable === null || disable === void 0 ? void 0 : disable.includes('zip')),
    internalLabel: true,
    required: required,
    country: addressData.country,
    onChange: value => handleFormChange('zip', value || ''),
    onValidate: validationState => handleValidationChange('zip', validationState),
    onFocus: () => setAutoSelectVerified(false),
    onBlur: () => setTimeout(() => setAutoSelectVerified(true), 300)
  })), /*#__PURE__*/React.createElement(AddressVerification, {
    autoSelectVerified: autocomplete && autoSelectVerified,
    autocomplete: autocomplete,
    verify: verify,
    address: address,
    hideValidAddressBanner: hideValidAddressBanner,
    onSelect: selected => onChange(selected),
    root: root
  }));
}